import { Box, Checkbox, Grid, Tab, Tabs, Typography } from '@mui/material';
import { DrawerHeader } from '../../../../../../components/Drawer/DrawerHeader';
import useEditSubstanceForm, { EditSubstanceFormData } from '../../hooks/useEditSubstanceForm';
import CustomField from '../../../../../../components/CustomFormsComponents/CustomField';
import { useEffect, useState } from 'react';
import ButtonActions from '../../../../../../components/ButtonActions';
import useNewStudyForm, { NewStudyFormData } from '../../hooks/useNewStudyForm';
import { useAppSelector } from '../../../../../../hooks/reduxHooks';
import { SubstanceStudyCard } from '../SubstanceStudyCard';
import Button from '../../../../../../components/button/Button';
import BackdropComponent from '../../../../../../components/Backdrop/BackdropComponent';
import { enqueueSnackbar } from 'notistack';
import { isStudiesTabEnabled } from '../../helpers';
import { TabPanel } from '../../../../../../components/TabPanel';

const SubstanceInformationDrawer = ({
    setOpen,
    selectedSubstance,
    translate,
    currentTab,
    handleTabChange,
    onlyVisual
}: any) => {
    const [showStudyForm, setShowStudysForm] = useState(false);
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const carcinogenicReport = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport
    );

    const carcinogenicParametricData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicParametricData
    );

    const selectedSubstanceId: number = selectedSubstance?.id;

    const handleCloseDrawer = () => {
        setOpen('');
    };

    const {
        formData: editSubstanceFormData,
        errors: editSubstanceErrors,
        editSubstanceFieldsConfig,
        handleChange: editSubstanceHandleChange,
        handleEditSubstance,
        isEditingSubstance,
        noComercialNameChecked,
        setNoComercialNameChecked
    } = useEditSubstanceForm({
        selectedSubstance,
        selectedSubstanceId,
        selectedCarcinogenicReportId,
        handleTabChange
    });

    const {
        formData: newStudyFormData,
        errors: newStudyErrors,
        newStudyFieldsConfig,
        handleChange: newStudyHandleChange,
        handleSubmitStudy,
        handleDeleteSubstanceIconClick,
        isCreatingNewSubstanceStudy,
        isDeletingSubstanceStudy
    } = useNewStudyForm({ selectedSubstanceId, selectedCarcinogenicReportId });

    const getFrequencyTypeDescriptionById = (id: number): string | null => {
        if (!carcinogenicParametricData?.tiposFrecuencia || !id) {
            return null;
        }
        const item = carcinogenicParametricData.tiposFrecuencia.find(
            (frequencyType) => frequencyType.id === id
        );
        return item ? item.descripcion : null;
    };

    const getStudyTypeName = (studyId: number) => {
        if (!studyId) return '';
        const studyType = carcinogenicParametricData?.tiposEstudio?.find(
            (studyType) => studyType.id === studyId
        );
        return studyType ? studyType.descripcion : '';
    };

    //this help to maintaing real time values without depending on local management
    const currentSubstanceItem = carcinogenicReport.sustancias.find(
        (item: any) => item?.id === selectedSubstance.id
    );

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            sx={{ flexDirection: 'column', height: '100%' }}
        >
            <Grid>
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={
                        currentTab === 0
                            ? translate('editSubstanceForm.titles.substance')
                            : translate('editSubstanceForm.titles.studies')
                    }
                />
                <Grid display="flex" direction={'row'} bgcolor={'#red'} pb={2} mt={4}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        sx={{ display: 'flex', width: '100%' }}
                    >
                        <Tab value={0} label="Sustancias" />
                        <Tab
                            value={1}
                            label="Estudios"
                            disabled={!isStudiesTabEnabled(selectedSubstance)}
                        />
                    </Tabs>
                </Grid>
                <TabPanel index={0} value={currentTab}>
                    <Box
                        display="flex"
                        flexDirection={'column'}
                        justifyContent="space-between"
                        width={'100%'}
                        p={1}
                        sx={{ flexDirection: 'column', height: '100%', minHeight: '100%' }}
                    >
                        <Grid mt={2} container spacing={2} rowSpacing={0.01}>
                            {editSubstanceFieldsConfig.map((field, index) => (
                                <Grid
                                    item
                                    xs={field?.gridSpace?.xs}
                                    sm={field?.gridSpace?.sm}
                                    md={field?.gridSpace?.md}
                                    lg={field?.gridSpace?.lg}
                                    key={index}
                                >
                                    <CustomField
                                        config={field}
                                        onlyVisual={onlyVisual}
                                        value={
                                            editSubstanceFormData[
                                                field.name as keyof EditSubstanceFormData
                                            ]
                                        }
                                        onChange={editSubstanceHandleChange}
                                        error={editSubstanceErrors[field.name as string]?.hasError}
                                        helperText={
                                            editSubstanceErrors[field.name as string]
                                                ?.errorMessage || field.helperText
                                        }
                                    />
                                    {field.name === 'comercialName' && (
                                        <Grid container alignItems={'center'} mt={-2} ml={-1.5}>
                                            <Checkbox
                                                onChange={() => {
                                                    if (onlyVisual) return;
                                                    if (noComercialNameChecked) {
                                                        editSubstanceHandleChange(
                                                            'comercialName',
                                                            ''
                                                        );
                                                        setNoComercialNameChecked(false);
                                                    } else {
                                                        editSubstanceHandleChange(
                                                            'comercialName',
                                                            'No Aplica'
                                                        );
                                                        setNoComercialNameChecked(true);
                                                    }
                                                }}
                                                checked={noComercialNameChecked}
                                            />
                                            <Typography variant="body1">
                                                Sin nombre comercial
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </TabPanel>
                <TabPanel index={1} value={currentTab}>
                    <Box
                        display="flex"
                        flexDirection={'column'}
                        justifyContent="space-between"
                        sx={{ flexDirection: 'column', height: '100%', minHeight: '100%' }}
                    >
                        <Grid>
                            <Grid maxHeight={'350px'} overflow={'auto'}>
                                <>
                                    {currentSubstanceItem?.estudios &&
                                        currentSubstanceItem?.estudios?.map((study: any) => (
                                            <Grid mt={2}>
                                                <SubstanceStudyCard
                                                    key={study?.id}
                                                    type={getStudyTypeName(study?.id)}
                                                    description={study?.detalle}
                                                    frequency={getFrequencyTypeDescriptionById(
                                                        study?.id
                                                    )}
                                                    frequencyQuantity={study?.cantidad}
                                                    handleDeleteClick={() => {
                                                        if (onlyVisual) return;
                                                        handleDeleteSubstanceIconClick(study?.id);
                                                    }}
                                                    onlyVisual={onlyVisual}
                                                />
                                            </Grid>
                                        ))}
                                </>
                            </Grid>
                            <Grid
                                display={'flex'}
                                container
                                width={'100%'}
                                justifyContent={'flex-end'}
                                mt={3}
                            >
                                {!onlyVisual && (
                                    <Button
                                        disabled={showStudyForm}
                                        type="contained"
                                        onClick={() => setShowStudysForm(true)}
                                        loading={false}
                                    >
                                        {translate('newStudyForm.buttons.new')}
                                    </Button>
                                )}
                            </Grid>

                            {showStudyForm && (
                                <Box
                                    bgcolor={'#F8F8F8'}
                                    borderRadius={'8px'}
                                    p={'24px 16px'}
                                    mt={2}
                                >
                                    <Grid container spacing={2} rowSpacing={0.01}>
                                        <Grid xs={12} pl={2}>
                                            <Typography variant="subtitle1">
                                                {translate('newStudyForm.formTitle')}
                                            </Typography>
                                        </Grid>
                                        {newStudyFieldsConfig.map((field, index) => (
                                            <Grid
                                                item
                                                xs={field?.gridSpace?.xs}
                                                sm={field?.gridSpace?.sm}
                                                md={field?.gridSpace?.md}
                                                lg={field?.gridSpace?.lg}
                                                key={index}
                                            >
                                                <CustomField
                                                    config={field}
                                                    value={
                                                        newStudyFormData[
                                                            field.name as keyof NewStudyFormData
                                                        ]
                                                    }
                                                    onChange={newStudyHandleChange}
                                                    error={
                                                        newStudyErrors[field.name as string]
                                                            ?.hasError
                                                    }
                                                    helperText={
                                                        newStudyErrors[field.name as string]
                                                            ?.errorMessage || field.helperText
                                                    }
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        sx={{ marginTop: '1rem' }}
                                    >
                                        <Box
                                            sx={{
                                                '& > :not(style)': { m: 1 },
                                                display: 'flex'
                                            }}
                                        >
                                            <ButtonActions
                                                renderBackAction
                                                returnText={translate(
                                                    'newStudyForm.buttons.cancel'
                                                )}
                                                confirmText={translate(
                                                    'newStudyForm.buttons.addStudy'
                                                )}
                                                loading={false}
                                                disabled={false}
                                                handleClose={() => setShowStudysForm(false)}
                                                onClick={handleSubmitStudy}
                                                borderRadius="4px"
                                                size="small"
                                            />
                                        </Box>
                                    </Grid>
                                </Box>
                            )}
                        </Grid>
                    </Box>
                </TabPanel>
            </Grid>
            <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ marginTop: '1rem' }}
            >
                {!onlyVisual && (
                    <Box
                        sx={{
                            '& > :not(style)': { m: 1 },
                            display: 'flex'
                        }}
                    >
                        {currentTab === 0 ? (
                            <ButtonActions
                                handleClose={handleCloseDrawer}
                                renderBackAction
                                returnText={translate('editSubstanceForm.buttons.cancel')}
                                confirmText={translate('editSubstanceForm.buttons.save')}
                                loading={false}
                                disabled={false}
                                onClick={handleEditSubstance}
                                borderRadius="4px"
                                size="small"
                            />
                        ) : currentTab === 1 ? (
                            <ButtonActions
                                handleClose={handleCloseDrawer}
                                renderBackAction
                                returnText={translate('editSubstanceForm.buttons.cancel')}
                                confirmText={translate('editSubstanceForm.buttons.save')}
                                loading={false}
                                disabled={false}
                                onClick={() => {
                                    if (currentSubstanceItem?.estudios?.length === 0) {
                                        enqueueSnackbar(
                                            translate('newStudyForm.enqueues.common.noStudies'),
                                            {
                                                variant: 'error'
                                            }
                                        );
                                    } else {
                                        handleCloseDrawer();
                                    }
                                }}
                                borderRadius="4px"
                                size="small"
                            />
                        ) : (
                            <></>
                        )}
                    </Box>
                )}
                <BackdropComponent
                    loading={
                        isEditingSubstance ||
                        isCreatingNewSubstanceStudy ||
                        isDeletingSubstanceStudy
                    }
                />
            </Grid>
        </Box>
    );
};

export { SubstanceInformationDrawer };
